import { ICellRendererParams } from 'ag-grid-community'
import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { IconButton, styled } from '@mui/material'
import ViewKanbanIcon from '@mui/icons-material/ViewKanban'
import { RootDiv } from '../../../../containers/commons/AgGrid/components/cell/CellRendererRoot'
import { AllState } from '../../../../../store'
import {
  StatusKanbanSearchConditionWithResponsible,
  openKanban,
} from '../../../Kanban/Kanban'
import {
  SprintReportMemberAlias,
  SprintReportValue,
} from '../../../../../lib/functions/sprintReport'
import { dateTermVoService } from '../../../../../domain/value-object/DateTermVO'
import { dateVoService } from '../../../../../domain/value-object/DateVO'
import { dateV2Service } from '../../../../../domain/value-object/DateV2'

type DateRange = { from: Date; to: Date }

type Props = ICellRendererParams & {
  dateTerm: DateRange
  rootUuid?: string
  teamUuid?: string
  searchConditionDateTerm?: {
    startDate?: number
    endDate?: number
  }
  onClickValue?: (
    values: { [date: string]: SprintReportValue },
    dateTerm: DateRange
  ) => void
  disabled: boolean
}

export const TotalCellRenderer = ({
  value,
  valueFormatted,
  dateTerm,
  rootUuid,
  teamUuid,
  searchConditionDateTerm,
  onClickValue,
  node,
  disabled,
}: Props) => {
  const projectCode = useSelector(
    (state: AllState) => state.project.current?.code
  )

  const onClickIcon = useCallback(() => {
    if (!projectCode) return
    const searchConditionDateTermVo = dateTermVoService.construct(
      searchConditionDateTerm?.startDate,
      searchConditionDateTerm?.endDate
    )
    const columnDateTermVo = dateTermVoService.construct(
      dateTerm.from,
      dateTerm.to
    )
    // Kanban の `to` は inclusive のため、列範囲の終端から1日戻して比較する
    const columnEndDate = dateVoService.toDate(columnDateTermVo.to!)
    const shiftedColumnEndDateVo = dateVoService.construct(
      dateV2Service.addDays(columnEndDate, -1)
    )
    const startDateVo =
      dateVoService.diff(
        columnDateTermVo.from!,
        searchConditionDateTermVo.from!
      ) >= 0
        ? columnDateTermVo.from
        : searchConditionDateTermVo.from
    const endDateVo =
      dateVoService.diff(
        shiftedColumnEndDateVo,
        searchConditionDateTermVo.to!
      ) <= 0
        ? shiftedColumnEndDateVo
        : searchConditionDateTermVo.to
    const query: Partial<StatusKanbanSearchConditionWithResponsible> = {
      rootUuid,
      teamUuid,
      scheduledEndDate: {
        from: startDateVo,
        to: endDateVo,
      },
      responsibleUuid:
        node.data.member.name === SprintReportMemberAlias.TOTAL
          ? ''
          : node.data.member.name === SprintReportMemberAlias.UNASSIGNED
            ? 'BLANK'
            : node.data.member.uuid,
    }
    openKanban(projectCode, query)
  }, [
    projectCode,
    rootUuid,
    teamUuid,
    dateTerm,
    node.data.member,
    searchConditionDateTerm,
  ])

  const onClick = useCallback(() => {
    if (!onClickValue) return
    onClickValue(node.data.values, dateTerm)
  }, [node.data.values, dateTerm, onClickValue])

  return (
    <Root>
      <IconButton onClick={onClickIcon} disabled={disabled}>
        <ViewKanbanIcon
          color={disabled ? 'disabled' : 'primary'}
          style={{ fontSize: 16 }}
        />
      </IconButton>
      <ClickableValue onClick={onClick} $disabled={disabled}>
        {valueFormatted ?? value}
      </ClickableValue>
    </Root>
  )
}

// アイコンと数値を左右に分けて配置するため、RootDiv に flex レイアウトを足したラッパー
const Root = styled(RootDiv)({
  display: 'flex',
  justifyContent: 'space-between',
})

const ClickableValue = styled('div', {
  shouldForwardProp: prop => prop !== '$disabled',
})<{ $disabled: boolean }>(({ $disabled }) => ({
  paddingLeft: '8px',
  color: $disabled ? 'inherit' : 'blue',
  cursor: $disabled ? 'inherit' : 'pointer',
}))
